import includes from 'lodash/includes';
import { useContext, useEffect } from 'react';
import { NavigateFunction, Outlet, useNavigate, useLocation } from 'react-router-dom';

import { RegistrationStatusEnum, UserDetail, UserTypeEnum } from '../api';
import { SessionContext } from '../app/contexts/SessionContext';

export const ALL_USER_TYPES = [
  UserTypeEnum.PROVIDER,
  UserTypeEnum.HEALTHCARE_ENTITY_REP,
  UserTypeEnum.STAFF,
];

interface Props {
  acceptedStatuses?: RegistrationStatusEnum[];
  allowedUserTypes?: UserTypeEnum[];
  permissionRequired?: string;
}

const handleUnacceptableStatus = (
  user: UserDetail,
  acceptedStatuses: RegistrationStatusEnum[],
  navigate: NavigateFunction
) => {
  if (user.registration_status === RegistrationStatusEnum.SETUP) {
    return navigate('/register/email-confirmation');
  }

  if (user.registration_status === RegistrationStatusEnum.PROVIDER_PROFILE) {
    window.location.href = '/app/provider/onboarding';
    return;
  }

  if (
    !includes(acceptedStatuses, RegistrationStatusEnum.MISSING_INFO) &&
    user.registration_status === RegistrationStatusEnum.MISSING_INFO
  ) {
    return navigate('/register/complete');
  }

  if (
    !includes(acceptedStatuses, RegistrationStatusEnum.PROVISIONAL) &&
    user.registration_status === RegistrationStatusEnum.PROVISIONAL &&
    user.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP
  ) {
    return navigate('/register/provisional/healthcare-entity-rep');
  }

  return navigate('/');
};

export const AuthenticatedRoute = (
  { acceptedStatuses = [], allowedUserTypes, permissionRequired }: Props
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, user, sessionExists } = useContext(SessionContext);

  useEffect(() => {
    if ((!isLoading && !user) || sessionExists === false) {
      const nextPath = encodeURIComponent(location.pathname + location.search);
      return navigate(`/login?next=${nextPath}`);
    }

    if (!!user) {
      if (acceptedStatuses.length > 0 && !includes(acceptedStatuses, user.registration_status)) {
        return handleUnacceptableStatus(user, acceptedStatuses, navigate);
      }

      if (!!allowedUserTypes && !includes(allowedUserTypes, user.user_type)) {
        return navigate('/');
      }

      if (permissionRequired && user?.permissions?.includes(permissionRequired) === false) {
        console.log('User does not have permission:', permissionRequired);
        return navigate('/');
      }
    }
  }, [isLoading, user, navigate, acceptedStatuses, allowedUserTypes, location, sessionExists]);

  return <Outlet />;
};
